'use client';

import VideoPlayer from '@commons/components/VideoPlayer';
import { useMediaQueries } from '@commons/contexts/responsive';
import type { DrupalBlock, DrupalVideo } from '@commons/types/drupal';

// Type definition for the RemoteVideoBlock component properties.

type Props = DrupalBlock<{
  url: string;
  name: string;
  mobileVideo?: DrupalVideo;
  hasMobileVideo?: boolean;
  loop?: boolean;
  autoplay?: boolean;
  startOnMute?: boolean;
  displayControls?: boolean;
  roundCorners?: boolean;
  aspectRatio?: string;
  fullscreen?: boolean;
}>;

/**
 * RemoteVideoBlock component.
 *
 * Supports multiple sources for responsive video handling.
 */

const RemoteVideoBlock = ({ content }: Props) => {
  const { ready, minWidth } = useMediaQueries();

  const isMobile = ready && !minWidth.lg;

  const {
    url,
    mobileVideo,
    hasMobileVideo = false,
    loop = false,
    autoplay = false,
    startOnMute = false,
    displayControls = true,
    roundCorners = true,
    aspectRatio,
    fullscreen = false,
  } = content;

  let videoUrl;
  if (isMobile && hasMobileVideo && mobileVideo) {
    videoUrl = mobileVideo.url;
  } else {
    videoUrl = url;
  }

  return (
    <VideoPlayer
      src={videoUrl}
      loop={loop}
      muted={startOnMute}
      volume={startOnMute ? 0 : 1}
      playing={autoplay}
      autoPlay={autoplay}
      controls={displayControls}
      playsInline
      fullScreen={fullscreen}
      aspectRatio={aspectRatio ?? '16 / 9'}
      roundCorners={roundCorners}
      displayLoading={false} // For some reason, `onReady` is not consistently called for remote videos.
      className="w-100"
    />
  );
};

export default RemoteVideoBlock;
